import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { superadminApi } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { ArrowLeft, ExternalLink, User, Anchor, Activity } from 'lucide-react';

interface OrgDetailData {
  org: Record<string, unknown>;
  marinas: Record<string, unknown>[];
  users: Record<string, unknown>[];
  usage_this_month: { chats: number; claude_cost_eur: number };
}

const TIER_BADGE: Record<string, string> = {
  free: 'bg-slate-100 text-slate-700',
  starter: 'bg-gray-100 text-gray-700',
  growth: 'bg-blue-100 text-blue-700',
  group: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};
const STATUS_BADGE: Record<string, string> = {
  trialing: 'bg-sky-100 text-sky-700',
  active: 'bg-green-100 text-green-700',
  past_due: 'bg-orange-100 text-orange-700',
  expired: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export function SuperadminOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [data, setData] = useState<OrgDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const toast = useToast();

  // Edit form state
  const [editTier, setEditTier] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editMaxMarinas, setEditMaxMarinas] = useState('');

  useEffect(() => {
    if (!orgId) return;
    superadminApi.getOrg(Number(orgId))
      .then((d) => {
        setData(d);
        setEditTier(d.org.subscription_tier as string ?? '');
        setEditStatus(d.org.subscription_status as string ?? '');
        setEditMaxMarinas(String(d.org.max_marinas ?? ''));
      })
      .catch(() => toast.error('Failed to load organization'))
      .finally(() => setLoading(false));
  }, [orgId]);

  const handleSaveEdit = async () => {
    if (!orgId || !data) return;
    // Only send fields that actually changed to keep audit logs clean
    const patch: Parameters<typeof superadminApi.updateOrg>[1] = {};
    if (editTier !== data.org.subscription_tier) patch.subscription_tier = editTier;
    if (editStatus !== data.org.subscription_status) patch.subscription_status = editStatus;
    const parsedMax = parseInt(editMaxMarinas, 10);
    if (!isNaN(parsedMax) && parsedMax !== data.org.max_marinas) patch.max_marinas = parsedMax;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    try {
      await superadminApi.updateOrg(Number(orgId), patch);
      toast.success('Organization updated');
      setEditing(false);
      // Reload
      const updated = await superadminApi.getOrg(Number(orgId));
      setData(updated);
    } catch {
      toast.error('Failed to update organization');
    }
  };

  const handleImpersonate = async () => {
    if (!orgId) return;
    const confirmed = window.confirm(
      'This will generate a 15-minute impersonation session for this org\'s admin. Continue?',
    );
    if (!confirmed) return;
    setImpersonating(true);
    try {
      const result = await superadminApi.impersonateOrg(Number(orgId));
      // Copy token to clipboard for use
      await navigator.clipboard.writeText(result.impersonation_token);
      toast.success(`Token copied! Impersonating ${result.target_email}. Expires: ${new Date(result.expires_at).toLocaleTimeString()}`);
    } catch {
      toast.error('Failed to generate impersonation session');
    } finally {
      setImpersonating(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading…</div>;
  if (!data) return <div className="p-8 text-center text-red-500">Organization not found</div>;

  const { org, marinas, users, usage_this_month } = data;
  const tier = org.subscription_tier as string;
  const status = org.subscription_status as string;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Back link + header */}
      <div>
        <Link to="/superadmin/orgs" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to organizations
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{org.name as string}</h1>
            <p className="text-sm text-gray-400 mt-1">/{org.slug as string}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(!editing)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 text-gray-700"
            >
              {editing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={handleImpersonate}
              disabled={impersonating}
              className="text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              {impersonating ? 'Generating…' : 'Impersonate'}
            </button>
          </div>
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-4">
          <h3 className="text-sm font-semibold text-amber-800">Edit Organization</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Subscription Tier</label>
              <select value={editTier} onChange={(e) => setEditTier(e.target.value)} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
                <option value="free">Free (booking only)</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="group">Group</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Subscription Status</label>
              <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past due</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Marinas</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={editMaxMarinas}
                onChange={(e) => setEditMaxMarinas(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5"
              />
            </div>
          </div>
          <button onClick={handleSaveEdit} className="text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg px-4 py-2">
            Save Changes
          </button>
        </div>
      )}

      {/* Info cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Plan</p>
          <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${TIER_BADGE[tier] ?? 'bg-gray-100 text-gray-600'}`}>{tier}</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Status</p>
          <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-600'}`}>{status.replace(/_/g, ' ')}</span>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Chats This Month</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{usage_this_month.chats}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Claude Cost (month)</p>
          <p className="text-xl font-bold text-gray-900 mt-1">€{usage_this_month.claude_cost_eur.toFixed(2)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Marinas */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Anchor className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Marinas ({marinas.length})</h2>
          </div>
          {marinas.length === 0 ? (
            <p className="text-sm text-gray-400">No marinas</p>
          ) : (
            <div className="space-y-2">
              {marinas.map((m) => (
                <div key={m.id as number} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-gray-800">{m.name as string}</span>
                    <span className="text-gray-400 ml-2">/{m.slug as string}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(m.stripe_onboarding_complete as boolean) && (
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Stripe</span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${(m.is_active as boolean) ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {(m.is_active as boolean) ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Users */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Team ({users.length})</h2>
          </div>
          {users.length === 0 ? (
            <p className="text-sm text-gray-400">No users</p>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id as string} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-gray-800">{u.name as string}</span>
                    <span className="text-gray-400 text-xs ml-2">{u.email as string}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">
                    {(u.role as string).replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Org metadata */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700">Details</h2>
        </div>
        <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          {[
            ['ID', String(org.id)],
            ['Country', (org.country as string) ?? '—'],
            ['Max Marinas', String(org.max_marinas ?? '—')],
            ['Trial Ends', org.trial_ends_at ? new Date(org.trial_ends_at as string).toLocaleDateString() : '—'],
            ['Created', org.created_at ? new Date(org.created_at as string).toLocaleDateString() : '—'],
            ['Stripe Customer', (org.stripe_customer_id as string) ?? '—'],
          ].map(([key, val]) => (
            <div key={key}>
              <dt className="text-gray-400 text-xs">{key}</dt>
              <dd className="font-medium text-gray-700 mt-0.5">{val}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
